import { APIEndpoints } from '@/@admin/types/constants';
import {
    AddStudioAIOrgAccessPayload,
    AdminToolsParams,
    DaniConfigActiveState,
    DaniConfigOverrideEntry,
    DaniConfigRevisionCreatePayload,
    DaniConfigRevisionEntity,
    DaniConfigRevisionUpdatePayload,
    DaniConfigSetActivePayload,
    DaniConfigSetOverridePayload,
    DaniConfigTestConnectionPayload,
    DaniConfigType,
    MessageMarkerEntity,
    MetricsTimePeriod,
    MetricsUserType,
    OrgFilterOptionDto,
    StudioAIChatHistoryEntity,
    StudioAIConversationReport,
    StudioAIFeedbackEntity,
    StudioAIMetricsEntity,
    StudioAIOrgAccessEntity,
    StudioAIOrgAccessType,
    StudioAIOrgAccessTypeCounts,
    StudioAIUserAccessEntity,
    UpdateStudioAIOrgAccessPayload,
} from '@/@admin/types/entities';
import {
    CreateMessageMarkerDto,
    GetMessageMarkersQuery,
    GetMessageMarkersResponse,
    UpdateMessageMarkerDto,
} from '@/@admin/types/message-markers';
import { buildCacheKey } from '@/@admin/utils';
import { ERROR_MESSAGES } from '@/@cms/types';
import { STUDIO_AI_NAME } from '@/@studio-ai/constants';
import { ConversationContextData } from '@/@studio-ai/types';
import { tracking } from '@/lib/features/tracking';
import { portalApi } from '@/lib/http/axios';
import { BaseService } from './base.service';

/**
 * Service class for Studio AI admin operations
 * Handles chat history, metrics, prompts, and message markers
 */
export class StudioAIService extends BaseService {
    constructor() {
        super(portalApi);
    }

    // Studio AI orgs for filter dropdowns (chat history, metrics)
    async getStudioAIOrgs(): Promise<OrgFilterOptionDto[]> {
        try {
            const cacheKey = buildCacheKey('adminFetchStudioAIOrgs', {});
            const cachedData = this.getCachedData(cacheKey);
            if (cachedData) return cachedData;

            const { data: response } = await this.axiosInstance.get<OrgFilterOptionDto[]>(
                APIEndpoints.STUDIO_AI_ORGS,
            );

            if ((response as unknown as { hasError?: boolean })?.hasError) {
                throw new Error('No orgs data received from the server');
            }
            const orgs = (Array.isArray(response) ? response : []) as OrgFilterOptionDto[];
            this.setCachedData(cacheKey, orgs);
            return orgs;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            tracking.errorWithSource(
                new Error(`Failed to get Studio AI orgs: ${errorMessage}`),
                'studio.StudioAIService.getStudioAIOrgs',
            );
            throw new Error(
                ERROR_MESSAGES.API_ERROR(`Failed to get Studio AI orgs: ${errorMessage}`),
            );
        }
    }

    // Studio AI Chat Services
    async getStudioAIChatHistory(params: AdminToolsParams): Promise<{
        data: StudioAIChatHistoryEntity[];
        pagination: {
            page: number;
            limit: number;
            total: number;
            totalPages: number;
        };
    }> {
        try {
            const cacheKey = buildCacheKey('adminFetchStudioAIChatHistory', params);
            const cachedData = this.getCachedData(cacheKey);
            if (cachedData) return cachedData;

            const { data: response } = await this.axiosInstance.get(
                APIEndpoints.STUDIO_AI_CHAT_HISTORY,
                {
                    params,
                },
            );

            if (response.hasError) {
                tracking.errorWithSource(
                    new Error(`No ${STUDIO_AI_NAME} chat history data received from the server.`),
                    'studio.StudioAIService.getStudioAIChatHistory',
                );
                throw new Error(`No ${STUDIO_AI_NAME} chat history data received from the server`);
            }

            // Extract the data array and pagination from the response structure
            const chatHistoryResponse = {
                data: (response?.data ?? []) as StudioAIChatHistoryEntity[],
                pagination: response?.pagination ?? {
                    page: 1,
                    limit: 100,
                    total: 0,
                    totalPages: 0,
                },
            };

            // Cache the full response (data + pagination)
            this.setCachedData(cacheKey, chatHistoryResponse);
            return chatHistoryResponse;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            tracking.errorWithSource(
                new Error(`Failed to get ${STUDIO_AI_NAME} chat history: ${errorMessage}`),
                'studio.StudioAIService.getStudioAIChatHistory',
            );
            throw new Error(
                ERROR_MESSAGES.API_ERROR(
                    `Failed to get ${STUDIO_AI_NAME} chat history: ${errorMessage}`,
                ),
            );
        }
    }

    async getStudioAIChatHistoryFeedback(
        conversationId: string,
    ): Promise<StudioAIFeedbackEntity[]> {
        try {
            const cacheKey = buildCacheKey('adminFetchStudioAIChatHistoryFeedback', {
                conversationId,
            });
            const cachedData = this.getCachedData(cacheKey);
            if (cachedData) return cachedData;

            const { data: response } = await this.axiosInstance.get(
                `${APIEndpoints.STUDIO_AI_CHAT_HISTORY}/${conversationId}/feedback`,
            );

            if (response.hasError) {
                tracking.errorWithSource(
                    new Error(
                        `No ${STUDIO_AI_NAME} chat history feedback data received from the server`,
                    ),
                    'studio.StudioAIService.getStudioAIChatHistoryFeedback',
                );
                throw new Error(
                    `No ${STUDIO_AI_NAME} chat history feedback data received from the server`,
                );
            }

            // Extract the data array from the response structure
            const chatHistoryFeedback = response as StudioAIFeedbackEntity[];
            this.setCachedData(cacheKey, chatHistoryFeedback);
            return chatHistoryFeedback;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            tracking.errorWithSource(
                new Error(`Failed to get ${STUDIO_AI_NAME} chat history feedback: ${errorMessage}`),
                'studio.StudioAIService.getStudioAIChatHistoryFeedback',
            );
            throw new Error(
                ERROR_MESSAGES.API_ERROR(
                    `Failed to get ${STUDIO_AI_NAME} chat history feedback: ${errorMessage}`,
                ),
            );
        }
    }

    /**
     * Pulls the rendered planner-report HTML stored against a conversation's
     * `agent_reports.html_content`. Returns `null` when the conversation has
     * no associated report row (or the row has no html). Cached briefly
     * because the html payload can be large.
     */
    async getStudioAIConversationReport(
        conversationId: string,
    ): Promise<StudioAIConversationReport | null> {
        try {
            const cacheKey = buildCacheKey('adminFetchStudioAIConversationReport', {
                conversationId,
            });
            const cachedData = this.getCachedData(cacheKey);
            if (cachedData !== undefined) return cachedData;

            const { data: response } = await this.axiosInstance.get(
                `${APIEndpoints.STUDIO_AI_CHAT_HISTORY}/${conversationId}/report`,
            );

            if (response?.hasError) {
                tracking.errorWithSource(
                    new Error(`Failed to load ${STUDIO_AI_NAME} conversation report`),
                    'studio.StudioAIService.getStudioAIConversationReport',
                );
                throw new Error(`Failed to load ${STUDIO_AI_NAME} conversation report`);
            }

            const report = (response as StudioAIConversationReport | null) ?? null;
            this.setCachedData(cacheKey, report);
            return report;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            tracking.errorWithSource(
                new Error(`Failed to get ${STUDIO_AI_NAME} conversation report: ${errorMessage}`),
                'studio.StudioAIService.getStudioAIConversationReport',
            );
            throw new Error(
                ERROR_MESSAGES.API_ERROR(
                    `Failed to get ${STUDIO_AI_NAME} conversation report: ${errorMessage}`,
                ),
            );
        }
    }

    async respondToFeedback(
        feedbackId: number,
        response: string,
    ): Promise<{ success: boolean; message: string }> {
        try {
            const { data: resp } = await this.axiosInstance.put(
                `${APIEndpoints.STUDIO_AI_FEEDBACK}/${feedbackId}/respond`,
                { response },
            );
            // Standardize error handling and payload extraction
            if (resp.hasError) {
                tracking.errorWithSource(
                    new Error(
                        ERROR_MESSAGES.API_ERROR(resp.message || 'Feedback response failed.'),
                    ),
                    'studio.StudioAIService.respondToFeedback',
                );
                throw new Error(
                    ERROR_MESSAGES.API_ERROR(resp.message || 'Feedback response failed.'),
                );
            }

            // Prevent stale lists after mutation
            this.clearCache();
            return resp as { success: boolean; message: string };
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            tracking.errorWithSource(
                new Error(`Failed to respond to feedback: ${errorMessage}`),
                'studio.StudioAIService.respondToFeedback',
            );
            throw new Error(
                ERROR_MESSAGES.API_ERROR(`Failed to respond to feedback: ${errorMessage}`),
            );
        }
    }

    async getStudioAIConversationHistory(conversationId: string): Promise<{
        messages: unknown[];
        prompt: string;
        contextData: ConversationContextData | null;
    }> {
        try {
            const cacheKey = buildCacheKey('adminFetchStudioAIConversationHistory', {
                conversationId,
            });
            const cachedData = this.getCachedData(cacheKey);
            if (cachedData) return cachedData;

            const { data: response } = await this.axiosInstance.get(
                `${APIEndpoints.STUDIO_AI_CONVERSATION_HISTORY}/${conversationId}`,
            );

            if (response.hasError) {
                tracking.errorWithSource(
                    new Error(
                        `No ${STUDIO_AI_NAME} conversation history data received from the server`,
                    ),
                    'studio.StudioAIService.getStudioAIConversationHistory',
                );
                throw new Error(
                    `No ${STUDIO_AI_NAME} conversation history data received from the server`,
                );
            }

            // Transform the response to match the expected format
            const conversationHistory = {
                messages: response.messages || [],
                prompt: response.prompt || '',
                contextData: response.contextData ?? null,
            };
            this.setCachedData(cacheKey, conversationHistory);
            return conversationHistory;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            tracking.errorWithSource(
                new Error(`Failed to get ${STUDIO_AI_NAME} conversation history: ${errorMessage}`),
                'studio.StudioAIService.getStudioAIConversationHistory',
            );
            throw new Error(
                ERROR_MESSAGES.API_ERROR(
                    `Failed to get ${STUDIO_AI_NAME} conversation history: ${errorMessage}`,
                ),
            );
        }
    }

    // Studio AI Metrics Services
    async getStudioAIMetrics(
        timePeriod: MetricsTimePeriod,
        userType: MetricsUserType = MetricsUserType.ALL,
        orgIds?: number[],
        includeInactive?: boolean,
    ): Promise<StudioAIMetricsEntity> {
        try {
            const orgIdsKey = orgIds && orgIds.length > 0 ? orgIds.join(',') : undefined;
            const cacheKey = buildCacheKey('adminFetchStudioAIMetrics', {
                timePeriod,
                userType,
                orgIds: orgIdsKey,
                includeInactive,
            });
            const cachedData = this.getCachedData(cacheKey);
            if (cachedData) return cachedData;

            const params: {
                timePeriod: MetricsTimePeriod;
                userType: MetricsUserType;
                orgIds?: string;
                includeInactive?: boolean;
            } = {
                timePeriod,
                userType,
            };

            // Only add orgIds if non-empty; backend accepts comma-separated: ?orgIds=123,456
            if (orgIds && orgIds.length > 0) {
                params.orgIds = orgIds.join(',');
            }

            // Only add includeInactive if true
            if (includeInactive) {
                params.includeInactive = includeInactive;
            }

            const { data: response } = await this.axiosInstance.get(
                APIEndpoints.STUDIO_AI_METRICS,
                {
                    params,
                },
            );

            if (response.hasError) {
                tracking.errorWithSource(
                    new Error(`No ${STUDIO_AI_NAME} metrics data received from the server`),
                    'studio.StudioAIService.getStudioAIMetrics',
                );
                throw new Error(`No ${STUDIO_AI_NAME} metrics data received from the server`);
            }

            this.setCachedData(cacheKey, response);
            return response as StudioAIMetricsEntity;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            tracking.errorWithSource(
                new Error(`Failed to get ${STUDIO_AI_NAME} metrics: ${errorMessage}`),
                'studio.StudioAIService.getStudioAIMetrics',
            );
            throw new Error(
                ERROR_MESSAGES.API_ERROR(
                    `Failed to get ${STUDIO_AI_NAME} metrics: ${errorMessage}`,
                ),
            );
        }
    }

    // Studio AI Message Marker Services
    async createMessageMarker(markerData: CreateMessageMarkerDto): Promise<MessageMarkerEntity> {
        try {
            const { data: response } = await this.axiosInstance.post<MessageMarkerEntity>(
                APIEndpoints.STUDIO_AI_MESSAGE_MARKERS,
                markerData,
            );

            if ((response as unknown as { hasError: boolean; message?: string })?.hasError) {
                tracking.errorWithSource(
                    new Error(
                        (response as unknown as { hasError: boolean; message?: string })?.message ||
                            'Message marker creation failed',
                    ),
                    'studio.StudioAIService.createMessageMarker',
                );
                throw new Error(
                    ERROR_MESSAGES.API_ERROR(
                        (response as unknown as { hasError: boolean; message?: string })?.message ||
                            'Message marker creation failed',
                    ),
                );
            }

            this.clearCache();
            return response as MessageMarkerEntity;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            tracking.errorWithSource(
                new Error(`Failed to create message marker: ${errorMessage}`),
                'studio.StudioAIService.createMessageMarker',
            );
            throw new Error(
                ERROR_MESSAGES.API_ERROR(`Failed to create message marker: ${errorMessage}`),
            );
        }
    }

    /**
     * Update an existing message marker
     */
    async updateMessageMarker(
        markerId: number,
        updateData: UpdateMessageMarkerDto,
    ): Promise<MessageMarkerEntity> {
        try {
            const { data: response } = await this.axiosInstance.put<MessageMarkerEntity>(
                `${APIEndpoints.STUDIO_AI_MESSAGE_MARKERS}/${markerId}`,
                updateData,
            );

            if ((response as unknown as { hasError: boolean; message?: string })?.hasError) {
                tracking.errorWithSource(
                    new Error(
                        (response as unknown as { hasError: boolean; message?: string })?.message ||
                            'Message marker update failed',
                    ),
                    'studio.StudioAIService.updateMessageMarker',
                );
                throw new Error(
                    ERROR_MESSAGES.API_ERROR(
                        (response as unknown as { hasError: boolean; message?: string })?.message ||
                            'Message marker update failed',
                    ),
                );
            }

            this.clearCache();
            return response;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            tracking.errorWithSource(
                new Error(`Failed to update message marker: ${errorMessage}`),
                'studio.StudioAIService.updateMessageMarker',
            );
            throw new Error(
                ERROR_MESSAGES.API_ERROR(`Failed to update message marker: ${errorMessage}`),
            );
        }
    }

    /**
     * Delete a message marker
     */
    async deleteMessageMarker(markerId: number): Promise<void> {
        try {
            await this.axiosInstance.delete(
                `${APIEndpoints.STUDIO_AI_MESSAGE_MARKERS}/${markerId}`,
            );
            this.clearCache();
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            tracking.errorWithSource(
                new Error(`Failed to delete message marker: ${errorMessage}`),
                'studio.StudioAIService.deleteMessageMarker',
            );
            throw new Error(
                ERROR_MESSAGES.API_ERROR(`Failed to delete message marker: ${errorMessage}`),
            );
        }
    }

    /**
     * Get all message markers with filtering
     */
    async getMessageMarkers(query?: GetMessageMarkersQuery): Promise<GetMessageMarkersResponse> {
        try {
            const cacheKey = buildCacheKey(
                'adminFetchMessageMarkers',
                (query ?? {}) as Record<string, string | number | boolean | null | undefined>,
            );
            const cachedData = this.getCachedData(cacheKey);
            if (cachedData) return cachedData;

            const { data: response } = await this.axiosInstance.get<GetMessageMarkersResponse>(
                APIEndpoints.STUDIO_AI_MESSAGE_MARKERS,
                {
                    params: query,
                },
            );

            if ((response as unknown as { hasError: boolean; message?: string })?.hasError) {
                tracking.errorWithSource(
                    new Error(
                        (response as unknown as { hasError: boolean; message?: string })?.message ||
                            'Message markers fetch failed',
                    ),
                    'studio.StudioAIService.getMessageMarkers',
                );
                throw new Error(
                    ERROR_MESSAGES.API_ERROR(
                        (response as unknown as { hasError: boolean; message?: string })?.message ||
                            'Message markers fetch failed',
                    ),
                );
            }

            this.setCachedData(cacheKey, response);
            return response;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            tracking.errorWithSource(
                new Error(`Failed to get message markers: ${errorMessage}`),
                'studio.StudioAIService.getMessageMarkers',
            );
            throw new Error(
                ERROR_MESSAGES.API_ERROR(`Failed to get message markers: ${errorMessage}`),
            );
        }
    }

    /**
     * Get message marker for a specific message
     */
    async getMessageMarkerByMessageId(
        conversationId: string,
        messageId: string,
    ): Promise<MessageMarkerEntity | null> {
        try {
            const cacheKey = buildCacheKey('adminFetchMessageMarkerByMessageId', {
                conversationId,
                messageId,
            });
            const cachedData = this.getCachedData(cacheKey);
            if (cachedData) return cachedData;

            const { data: response } = await this.axiosInstance.get<MessageMarkerEntity | null>(
                `${APIEndpoints.STUDIO_AI_MESSAGE_MARKERS}/by-message/${conversationId}/${messageId}`,
            );

            if ((response as unknown as { hasError: boolean; message?: string })?.hasError) {
                tracking.errorWithSource(
                    new Error(
                        (response as unknown as { hasError: boolean; message?: string })?.message ||
                            'Message marker by message ID fetch failed',
                    ),
                    'studio.StudioAIService.getMessageMarkerByMessageId',
                );
                throw new Error(
                    ERROR_MESSAGES.API_ERROR(
                        (response as unknown as { hasError: boolean; message?: string })?.message ||
                            'Message marker by message ID fetch failed',
                    ),
                );
            }

            this.setCachedData(cacheKey, response);
            return response;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            tracking.errorWithSource(
                new Error(`Failed to get message marker by message ID: ${errorMessage}`),
                'studio.StudioAIService.getMessageMarkerByMessageId',
            );
            throw new Error(
                ERROR_MESSAGES.API_ERROR(
                    `Failed to get message marker by message ID: ${errorMessage}`,
                ),
            );
        }
    }

    // DANI Config Services
    /**
     * List DANI config revisions (optionally filtered by type)
     */
    async getDaniConfigRevisions(type?: DaniConfigType): Promise<DaniConfigRevisionEntity[]> {
        try {
            const params: Record<string, string> = {};
            if (type) params.type = type;

            const cacheKey = buildCacheKey('adminFetchDaniConfigRevisions', params);
            const cachedData = this.getCachedData(cacheKey);
            if (cachedData) return cachedData;

            const { data: response } = await this.axiosInstance.get<DaniConfigRevisionEntity[]>(
                APIEndpoints.DANI_CONFIG_REVISIONS,
                { params },
            );

            const revisions = Array.isArray(response) ? response : [];
            this.setCachedData(cacheKey, revisions);
            return revisions;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            tracking.errorWithSource(
                new Error(`Failed to get DANI config revisions: ${errorMessage}`),
                'studio.StudioAIService.getDaniConfigRevisions',
            );
            throw new Error(
                ERROR_MESSAGES.API_ERROR(`Failed to get DANI config revisions: ${errorMessage}`),
            );
        }
    }

    /**
     * Get a single DANI config revision by ID
     */
    async getDaniConfigRevisionById(id: number): Promise<DaniConfigRevisionEntity> {
        try {
            const cacheKey = buildCacheKey('adminFetchDaniConfigRevisionById', { id });
            const cachedData = this.getCachedData(cacheKey);
            if (cachedData) return cachedData;

            const { data: response } = await this.axiosInstance.get<DaniConfigRevisionEntity>(
                `${APIEndpoints.DANI_CONFIG_REVISIONS}/${id}`,
            );

            this.setCachedData(cacheKey, response);
            return response;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            tracking.errorWithSource(
                new Error(`Failed to get DANI config revision: ${errorMessage}`),
                'studio.StudioAIService.getDaniConfigRevisionById',
            );
            throw new Error(
                ERROR_MESSAGES.API_ERROR(`Failed to get DANI config revision: ${errorMessage}`),
            );
        }
    }

    /**
     * Create a new DANI config revision
     */
    async createDaniConfigRevision(
        payload: DaniConfigRevisionCreatePayload,
    ): Promise<DaniConfigRevisionEntity> {
        try {
            const { data: response } = await this.axiosInstance.post<DaniConfigRevisionEntity>(
                APIEndpoints.DANI_CONFIG_REVISIONS,
                payload,
            );

            this.clearCache();
            return response;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            tracking.errorWithSource(
                new Error(`Failed to create DANI config revision: ${errorMessage}`),
                'studio.StudioAIService.createDaniConfigRevision',
            );
            throw new Error(
                ERROR_MESSAGES.API_ERROR(`Failed to create DANI config revision: ${errorMessage}`),
            );
        }
    }

    /**
     * Update an existing DANI config revision
     */
    async updateDaniConfigRevision(
        id: number,
        payload: DaniConfigRevisionUpdatePayload,
    ): Promise<DaniConfigRevisionEntity> {
        try {
            const { data: response } = await this.axiosInstance.put<DaniConfigRevisionEntity>(
                `${APIEndpoints.DANI_CONFIG_REVISIONS}/${id}`,
                payload,
            );

            this.clearCache();
            return response;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            tracking.errorWithSource(
                new Error(`Failed to update DANI config revision: ${errorMessage}`),
                'studio.StudioAIService.updateDaniConfigRevision',
            );
            throw new Error(
                ERROR_MESSAGES.API_ERROR(`Failed to update DANI config revision: ${errorMessage}`),
            );
        }
    }

    /**
     * Get active config state for a given type
     */
    async getDaniConfigActive(type: DaniConfigType): Promise<DaniConfigActiveState> {
        try {
            const cacheKey = buildCacheKey('adminFetchDaniConfigActive', { type });
            const cachedData = this.getCachedData(cacheKey);
            if (cachedData) return cachedData;

            const { data: response } = await this.axiosInstance.get<DaniConfigActiveState>(
                `${APIEndpoints.DANI_CONFIG_ACTIVE}/${type}`,
            );

            this.setCachedData(cacheKey, response);
            return response;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            tracking.errorWithSource(
                new Error(`Failed to get active DANI config: ${errorMessage}`),
                'studio.StudioAIService.getDaniConfigActive',
            );
            throw new Error(
                ERROR_MESSAGES.API_ERROR(`Failed to get active DANI config: ${errorMessage}`),
            );
        }
    }

    /**
     * Set active revision for a config type
     */
    async setDaniConfigActive(payload: DaniConfigSetActivePayload): Promise<{ ok: boolean }> {
        try {
            const { data: response } = await this.axiosInstance.put<{ ok: boolean }>(
                APIEndpoints.DANI_CONFIG_ACTIVE,
                payload,
            );

            this.clearCache();
            return response;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            tracking.errorWithSource(
                new Error(`Failed to set active DANI config: ${errorMessage}`),
                'studio.StudioAIService.setDaniConfigActive',
            );
            throw new Error(
                ERROR_MESSAGES.API_ERROR(`Failed to set active DANI config: ${errorMessage}`),
            );
        }
    }

    /**
     * Test connection to Snowflake Cortex with given config values
     */
    async testDaniConfigConnection(
        payload: DaniConfigTestConnectionPayload,
    ): Promise<{ ok: boolean; message?: string }> {
        try {
            const { data: response } = await this.axiosInstance.post<{
                ok: boolean;
                message?: string;
            }>(APIEndpoints.DANI_CONFIG_TEST_CONNECTION, payload);

            return response;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            tracking.errorWithSource(
                new Error(`Failed to test DANI config connection: ${errorMessage}`),
                'studio.StudioAIService.testDaniConfigConnection',
            );
            throw new Error(
                ERROR_MESSAGES.API_ERROR(`Failed to test DANI config connection: ${errorMessage}`),
            );
        }
    }

    // ─── DANI Config Override Services ──────────────────────────────────────

    async getDaniUserConfigOverrides(): Promise<DaniConfigOverrideEntry[]> {
        try {
            const { data } = await this.axiosInstance.get<DaniConfigOverrideEntry[]>(
                APIEndpoints.DANI_CONFIG_USER_OVERRIDES,
            );
            return Array.isArray(data) ? data : [];
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            tracking.errorWithSource(
                new Error(`Failed to get user config overrides: ${errorMessage}`),
                'studio.StudioAIService.getDaniUserConfigOverrides',
            );
            throw new Error(
                ERROR_MESSAGES.API_ERROR(`Failed to get user config overrides: ${errorMessage}`),
            );
        }
    }

    async setDaniUserConfigOverride(
        orgUserId: number,
        payload: DaniConfigSetOverridePayload,
    ): Promise<DaniConfigOverrideEntry> {
        try {
            const { data } = await this.axiosInstance.put<DaniConfigOverrideEntry>(
                `${APIEndpoints.DANI_CONFIG_USER_OVERRIDES}/${orgUserId}`,
                payload,
            );
            this.clearCache();
            return data;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            tracking.errorWithSource(
                new Error(`Failed to set user config override: ${errorMessage}`),
                'studio.StudioAIService.setDaniUserConfigOverride',
            );
            throw new Error(
                ERROR_MESSAGES.API_ERROR(`Failed to set user config override: ${errorMessage}`),
            );
        }
    }

    async removeDaniUserConfigOverride(orgUserId: number): Promise<void> {
        try {
            await this.axiosInstance.delete(
                `${APIEndpoints.DANI_CONFIG_USER_OVERRIDES}/${orgUserId}`,
            );
            this.clearCache();
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            tracking.errorWithSource(
                new Error(`Failed to remove user config override: ${errorMessage}`),
                'studio.StudioAIService.removeDaniUserConfigOverride',
            );
            throw new Error(
                ERROR_MESSAGES.API_ERROR(`Failed to remove user config override: ${errorMessage}`),
            );
        }
    }

    async getDaniOrgConfigOverrides(): Promise<DaniConfigOverrideEntry[]> {
        try {
            const { data } = await this.axiosInstance.get<DaniConfigOverrideEntry[]>(
                APIEndpoints.DANI_CONFIG_ORG_OVERRIDES,
            );
            return Array.isArray(data) ? data : [];
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            tracking.errorWithSource(
                new Error(`Failed to get org config overrides: ${errorMessage}`),
                'studio.StudioAIService.getDaniOrgConfigOverrides',
            );
            throw new Error(
                ERROR_MESSAGES.API_ERROR(`Failed to get org config overrides: ${errorMessage}`),
            );
        }
    }

    async setDaniOrgConfigOverride(
        orgId: number,
        payload: DaniConfigSetOverridePayload,
    ): Promise<DaniConfigOverrideEntry> {
        try {
            const { data } = await this.axiosInstance.put<DaniConfigOverrideEntry>(
                `${APIEndpoints.DANI_CONFIG_ORG_OVERRIDES}/${orgId}`,
                payload,
            );
            this.clearCache();
            return data;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            tracking.errorWithSource(
                new Error(`Failed to set org config override: ${errorMessage}`),
                'studio.StudioAIService.setDaniOrgConfigOverride',
            );
            throw new Error(
                ERROR_MESSAGES.API_ERROR(`Failed to set org config override: ${errorMessage}`),
            );
        }
    }

    async removeDaniOrgConfigOverride(orgId: number): Promise<void> {
        try {
            await this.axiosInstance.delete(`${APIEndpoints.DANI_CONFIG_ORG_OVERRIDES}/${orgId}`);
            this.clearCache();
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            tracking.errorWithSource(
                new Error(`Failed to remove org config override: ${errorMessage}`),
                'studio.StudioAIService.removeDaniOrgConfigOverride',
            );
            throw new Error(
                ERROR_MESSAGES.API_ERROR(`Failed to remove org config override: ${errorMessage}`),
            );
        }
    }

    // Studio AI User Access Services
    /**
     * Get all Studio AI user access records with pagination and filtering
     */
    async getUserAccessList(params?: {
        page?: number;
        limit?: number;
        searchTerm?: string;
        isEnabled?: boolean;
    }): Promise<{
        data: StudioAIUserAccessEntity[];
        pagination: {
            page: number;
            limit: number;
            total: number;
            totalPages: number;
        };
    }> {
        try {
            const cacheKey = buildCacheKey('adminFetchUserAccessList', params ?? {});
            const cachedData = this.getCachedData(cacheKey);
            if (cachedData) return cachedData;

            const { data: response } = await this.axiosInstance.get(
                APIEndpoints.STUDIO_AI_USER_ACCESS,
                { params },
            );

            if (response.hasError) {
                tracking.errorWithSource(
                    new Error(
                        response.message || `${STUDIO_AI_NAME} User Access list fetch failed.`,
                    ),
                    'studio.StudioAIService.getUserAccessList',
                );
                throw new Error(
                    response.message || `${STUDIO_AI_NAME} User Access list fetch failed.`,
                );
            }

            this.setCachedData(cacheKey, response);
            return response;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            tracking.errorWithSource(
                new Error(`Failed to get ${STUDIO_AI_NAME} User Access list: ${errorMessage}`),
                'studio.StudioAIService.getUserAccessList',
            );
            throw new Error(
                ERROR_MESSAGES.API_ERROR(
                    `Failed to get ${STUDIO_AI_NAME} User Access list: ${errorMessage}`,
                ),
            );
        }
    }

    /**
     * Add a user to Studio AI access
     */
    async addUserAccess(email: string, notes?: string): Promise<StudioAIUserAccessEntity> {
        try {
            const { data: response } = await this.axiosInstance.post(
                APIEndpoints.STUDIO_AI_USER_ACCESS,
                { email, notes },
            );

            if (response.hasError) {
                tracking.errorWithSource(
                    new Error(response.message || `${STUDIO_AI_NAME} User Access creation failed.`),
                    'studio.StudioAIService.addUserAccess',
                );
                throw new Error(
                    response.message || `${STUDIO_AI_NAME} User Access creation failed.`,
                );
            }

            this.clearCache();
            return response as StudioAIUserAccessEntity;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            tracking.errorWithSource(
                new Error(`Failed to add ${STUDIO_AI_NAME} User Access: ${errorMessage}`),
                'studio.StudioAIService.addUserAccess',
            );
            throw new Error(
                ERROR_MESSAGES.API_ERROR(
                    `Failed to add ${STUDIO_AI_NAME} User Access: ${errorMessage}`,
                ),
            );
        }
    }

    /**
     * Update user access (enable/disable)
     */
    async updateUserAccess(
        accessId: number,
        isEnabled: boolean,
        countInMetrics: boolean,
        notes?: string,
    ): Promise<StudioAIUserAccessEntity> {
        try {
            const { data: response } = await this.axiosInstance.put(
                `${APIEndpoints.STUDIO_AI_USER_ACCESS}/${accessId}`,
                { isEnabled, countInMetrics, notes },
            );

            if (response.hasError) {
                tracking.errorWithSource(
                    new Error(response.message || `${STUDIO_AI_NAME} User Access update failed.`),
                    'studio.StudioAIService.updateUserAccess',
                );
                throw new Error(response.message || `${STUDIO_AI_NAME} User Access update failed.`);
            }

            this.clearCache();
            return response as StudioAIUserAccessEntity;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            tracking.errorWithSource(
                new Error(`Failed to update ${STUDIO_AI_NAME} User Access: ${errorMessage}`),
                'studio.StudioAIService.updateUserAccess',
            );
            throw new Error(
                ERROR_MESSAGES.API_ERROR(
                    `Failed to update ${STUDIO_AI_NAME} User Access: ${errorMessage}`,
                ),
            );
        }
    }

    async getOrgAccessList(params?: {
        page?: number;
        limit?: number;
        searchTerm?: string;
        isEnabled?: boolean;
        orgId?: number;
        accessType?: StudioAIOrgAccessType;
    }): Promise<{
        data: StudioAIOrgAccessEntity[];
        pagination: { page: number; limit: number; total: number; totalPages: number };
    }> {
        try {
            const cacheKey = buildCacheKey('adminFetchOrgAccessList', params ?? {});
            const cachedData = this.getCachedData(cacheKey);
            if (cachedData) return cachedData;

            const { data: response } = await this.axiosInstance.get(
                APIEndpoints.STUDIO_AI_ORG_ACCESS,
                { params },
            );

            if (response.hasError) {
                tracking.errorWithSource(
                    new Error(
                        response.message || `${STUDIO_AI_NAME} Org Access list fetch failed.`,
                    ),
                    'studio.StudioAIService.getOrgAccessList',
                );
                throw new Error(
                    response.message || `${STUDIO_AI_NAME} Org Access list fetch failed.`,
                );
            }

            this.setCachedData(cacheKey, response);
            return response;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            tracking.errorWithSource(
                new Error(`Failed to get ${STUDIO_AI_NAME} Org Access list: ${errorMessage}`),
                'studio.StudioAIService.getOrgAccessList',
            );
            throw new Error(
                ERROR_MESSAGES.API_ERROR(
                    `Failed to get ${STUDIO_AI_NAME} Org Access list: ${errorMessage}`,
                ),
            );
        }
    }

    async addOrgAccess(payload: AddStudioAIOrgAccessPayload): Promise<StudioAIOrgAccessEntity> {
        try {
            const { data: response } = await this.axiosInstance.post(
                APIEndpoints.STUDIO_AI_ORG_ACCESS,
                payload,
            );

            if (response.hasError) {
                tracking.errorWithSource(
                    new Error(response.message || `${STUDIO_AI_NAME} Org Access creation failed.`),
                    'studio.StudioAIService.addOrgAccess',
                );
                throw new Error(
                    response.message || `${STUDIO_AI_NAME} Org Access creation failed.`,
                );
            }

            this.clearCache();
            return response as StudioAIOrgAccessEntity;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            tracking.errorWithSource(
                new Error(`Failed to add ${STUDIO_AI_NAME} Org Access: ${errorMessage}`),
                'studio.StudioAIService.addOrgAccess',
            );
            throw new Error(
                ERROR_MESSAGES.API_ERROR(
                    `Failed to add ${STUDIO_AI_NAME} Org Access: ${errorMessage}`,
                ),
            );
        }
    }

    async updateOrgAccess(
        accessId: number,
        payload: UpdateStudioAIOrgAccessPayload,
    ): Promise<StudioAIOrgAccessEntity> {
        try {
            const { data: response } = await this.axiosInstance.put(
                `${APIEndpoints.STUDIO_AI_ORG_ACCESS}/${accessId}`,
                payload,
            );

            if (response.hasError) {
                tracking.errorWithSource(
                    new Error(response.message || `${STUDIO_AI_NAME} Org Access update failed.`),
                    'studio.StudioAIService.updateOrgAccess',
                );
                throw new Error(response.message || `${STUDIO_AI_NAME} Org Access update failed.`);
            }

            this.clearCache();
            return response as StudioAIOrgAccessEntity;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            tracking.errorWithSource(
                new Error(`Failed to update ${STUDIO_AI_NAME} Org Access: ${errorMessage}`),
                'studio.StudioAIService.updateOrgAccess',
            );
            throw new Error(
                ERROR_MESSAGES.API_ERROR(
                    `Failed to update ${STUDIO_AI_NAME} Org Access: ${errorMessage}`,
                ),
            );
        }
    }

    async getOrgAccessTypeCounts(): Promise<StudioAIOrgAccessTypeCounts> {
        try {
            const { data: response } = await this.axiosInstance.get(
                `${APIEndpoints.STUDIO_AI_ORG_ACCESS}/stats`,
            );

            if (response.hasError) {
                throw new Error(
                    response.message || `${STUDIO_AI_NAME} Org Access stats fetch failed.`,
                );
            }

            return response as StudioAIOrgAccessTypeCounts;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            tracking.errorWithSource(
                new Error(`Failed to get ${STUDIO_AI_NAME} Org Access stats: ${errorMessage}`),
                'studio.StudioAIService.getOrgAccessTypeCounts',
            );
            throw new Error(
                ERROR_MESSAGES.API_ERROR(
                    `Failed to get ${STUDIO_AI_NAME} Org Access stats: ${errorMessage}`,
                ),
            );
        }
    }
}

export const studioAIService = new StudioAIService();
